import React, { useContext, useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  Modal,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Typography,
  Alert,
} from "@mui/material";
import { CheckCircle, Schedule, PersonAdd } from "@mui/icons-material";
import {
  addDoc,
  deleteDoc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import { createMeta, updateMeta } from "../utils/auditFields";
import PropertyContext from "../contexts/PropertyContext";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const EmployeeSchedule = () => {
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeName, setEmployeeName] = useState("");
  const [username, setUsername] = useState("");
  const [employeeType, setEmployeeType] = useState("");
  const [schedule, setSchedule] = useState({});
  const [idCounter, setIdCounter] = useState(1);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [selectedEmployeeForUser, setSelectedEmployeeForUser] = useState(null);
  const [userCreationEmail, setUserCreationEmail] = useState("");
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [message, setMessage] = useState("");

  //get property details
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  // useEffect to get employees
  useEffect(() => {
    const fetchEmployees = async () => {
      const employeesRef = propertyCollection(
        selectedClient,
        selectedProperty,
        "employees"
      );
      const unsubscribeToEmployees = onSnapshot(employeesRef, (snapshot) => {
        const employees = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEmployees(employees);
      });
    };
    fetchEmployees();
  }, [selectedClient, selectedProperty]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleEditOpen = (employee) => {
    setSelectedEmployee(employee);
    setSchedule(employee.schedule);
    setEditOpen(true);
  };
  const handleEditClose = () => setEditOpen(false);

  const handleAddEmployee = async () => {
    if (employeeName && employeeType) {
      const newEmployee = {
        name: employeeName,
        username: username || "",
        type: employeeType,
        schedule: schedule,
        ...createMeta(),
      };
      // Add employee to Firestore
      const employeesRef = propertyCollection(
        selectedClient,
        selectedProperty,
        "employees"
      );
      await addDoc(employeesRef, newEmployee);

      // setEmployees([...employees, newEmployee]);
      // setIdCounter(idCounter + 1);
      resetForm();
      handleClose();
    } else {
      alert("Please fill in all fields");
    }
  };
  const handleRemoveEmployee = (id) => {
    // setEmployees(employees.filter((employee) => employee.id !== id));
    // Remove employee from Firestore
    const employeeRef = propertyDoc(
      selectedClient,
      selectedProperty,
      "employees",
      id
    );
    deleteDoc(employeeRef);
    resetForm();
    handleEditClose();
  };

  const handleEditEmployee = async () => {
    if (selectedEmployee.name && selectedEmployee.type) {
      const updatedEmployee = {
        name: selectedEmployee.name,
        username: selectedEmployee.username || "",
        type: selectedEmployee.type,
        schedule: schedule,
        ...updateMeta(),
      };
      // Update employee in Firestore
      const employeeRef = propertyDoc(
        selectedClient,
        selectedProperty,
        "employees",
        selectedEmployee.id
      );
      await updateDoc(employeeRef, updatedEmployee);

      resetForm();
      handleEditClose();
    }
  };
  const resetForm = () => {
    setEmployeeName("");
    setUsername("");
    setEmployeeType("");
    setSchedule({});
  };

  const handleCheckboxChange = (day) => {
    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      [day]: prevSchedule[day] ? null : { startTime: "", endTime: "" },
    }));
  };

  const handleTimeChange = (day, field, value) => {
    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      [day]: {
        ...prevSchedule[day],
        [field]: value,
      },
    }));
  };

  const formatTime = (time) => {
    const [hour, minute] = time.split(":");
    const period = +hour >= 12 ? "PM" : "AM";
    const formattedHour = +hour % 12 || 12;
    return `${formattedHour}:${minute} ${period}`;
  };

  const columns = [
    {
      field: "accountStatus",
      headerName: "Account",
      width: 100,
      renderCell: (params) => {
        if (params.row.userId && params.row.userAccountStatus === "active") {
          return (
            <Chip
              label="Active"
              color="success"
              size="small"
              icon={<CheckCircle />}
            />
          );
        } else if (params.row.userAccountStatus === "invited") {
          return (
            <Chip
              label="Invited"
              color="warning"
              size="small"
              icon={<Schedule />}
            />
          );
        }
        return null;
      }
    },
    { field: "name", headerName: "Employee Name", width: 200 },
    // { field: "username", headerName: "Username", width: 200 },
    { field: "type", headerName: "Employee Type", width: 150 },
    ...daysOfWeek.map((day) => ({
      field: day.toLowerCase(),
      headerName: day,
      width: 150,
      valueGetter: (params) =>
        params.row.schedule[day]?.startTime && params.row.schedule[day]?.endTime
          ? `${formatTime(params.row.schedule[day].startTime)} - ${formatTime(
              params.row.schedule[day].endTime
            )}`
          : "",
    })),
  ];

  const log = () => {
    console.log(employees);
  };

  const handleOpenCreateUser = (employee) => {
    setSelectedEmployeeForUser(employee);
    setUserCreationEmail("");
    setMessage("");
    setCreateUserOpen(true);
  };

  const handleCloseCreateUser = () => {
    setCreateUserOpen(false);
    setSelectedEmployeeForUser(null);
    setUserCreationEmail("");
    setMessage("");
  };

  const handleCreateUserFromEmployee = async () => {
    if (!userCreationEmail || !selectedEmployeeForUser) {
      setMessage("Error: Please provide an email address");
      return;
    }

    setIsCreatingUser(true);
    setMessage("");

    try {
      const functions = getFunctions();
      const sendUserInvite = httpsCallable(functions, "sendUserInvite");

      // Parse name into first and last name
      const nameParts = selectedEmployeeForUser.name.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || nameParts[0];

      // Map employee type to user type (direct 1:1 mapping)
      const userType = selectedEmployeeForUser.type;

      const userInviteData = {
        firstName,
        lastName,
        email: userCreationEmail,
        userName: userCreationEmail.split('@')[0],
        userType,
        properties: [{
          displayName: selectedProperty,
          id: selectedProperty,
          clientId: selectedClient
        }],
        clientId: selectedClient,
        invitedBy: auth.currentUser?.email,
        // Employee linking fields
        employeeId: selectedEmployeeForUser.id,
        propertyId: selectedProperty,
        createdFromEmployee: true
      };

      const result = await sendUserInvite(userInviteData);

      if (result.data.success) {
        // Update employee record with invite status
        const employeeRef = propertyDoc(
          selectedClient,
          selectedProperty,
          "employees",
          selectedEmployeeForUser.id
        );
        await updateDoc(employeeRef, {
          userAccountStatus: "invited",
          inviteId: result.data.inviteId
        });

        setMessage("User invitation sent successfully!");
        setTimeout(() => {
          handleCloseCreateUser();
        }, 2000);
      } else {
        setMessage("Error: " + (result.data.error || "Failed to send invitation"));
      }
    } catch (error) {
      console.error("Error creating user invite:", error);
      setMessage("Error: " + error.message);
    }

    setIsCreatingUser(false);
  };

  return (
    <Box>
      <Button variant="contained" onClick={handleOpen}>
        Add Employee
      </Button>
      <Box mt={2} style={{ height: 400, width: "100%" }}>
        <DataGrid
          rows={employees}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          onRowClick={(params) => handleEditOpen(params.row)}
        />
      </Box>
      <Button variant="contained" onClick={log}>
        Log Employees
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Paper
          sx={{
            p: 4,
            position: "absolute",
            top: "10%",
            left: "50%",
            transform: "translate(-50%, -10%)",
            width: 600,
          }}
        >
          <h2>Add Employee</h2>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <TextField
              label="Employee Name"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              margin="normal"
              sx={{ mr: 1, flex: 1 }}
            />
            {/* <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              sx={{ ml: 1, flex: 1 }}
            /> */}
          </Box>
          <FormControl fullWidth margin="normal">
            <InputLabel>Employee Type</InputLabel>
            <Select
              value={employeeType}
              onChange={(e) => setEmployeeType(e.target.value)}
            >
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="housekeeping">Housekeeping</MenuItem>
              <MenuItem value="frontDesk">Front Desk</MenuItem>
            </Select>
          </FormControl>
          <Box>
            {daysOfWeek.map((day) => (
              <Box key={day} display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!schedule[day]}
                      onChange={() => handleCheckboxChange(day)}
                    />
                  }
                  label={day}
                />
                {schedule[day] && (
                  <Box display="flex" ml={2}>
                    <TextField
                      label="Start Time"
                      type="time"
                      value={schedule[day].startTime}
                      onChange={(e) =>
                        handleTimeChange(day, "startTime", e.target.value)
                      }
                      margin="normal"
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      value={schedule[day].endTime}
                      onChange={(e) =>
                        handleTimeChange(day, "endTime", e.target.value)
                      }
                      margin="normal"
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            onClick={handleAddEmployee}
            sx={{ mt: 2 }}
          >
            Add Employee
          </Button>
        </Paper>
      </Modal>

      {selectedEmployee && (
        <Modal open={editOpen} onClose={handleEditClose}>
          <Paper
            sx={{
              p: 4,
              position: "absolute",
              top: "10%",
              left: "50%",
              transform: "translate(-50%, -10%)",
              width: 600,
            }}
          >
            <h2>Edit Employee</h2>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <TextField
                label="Employee Name"
                value={selectedEmployee.name}
                onChange={(e) =>
                  setSelectedEmployee({
                    ...selectedEmployee,
                    name: e.target.value,
                  })
                }
                margin="normal"
                sx={{ mr: 1, flex: 1 }}
              />
              <TextField
                label="Username"
                value={selectedEmployee.username}
                onChange={(e) =>
                  setSelectedEmployee({
                    ...selectedEmployee,
                    username: e.target.value,
                  })
                }
                margin="normal"
                sx={{ ml: 1, flex: 1 }}
              />
            </Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>Employee Type</InputLabel>
              <Select
                value={selectedEmployee.type}
                onChange={(e) =>
                  setSelectedEmployee({
                    ...selectedEmployee,
                    type: e.target.value,
                  })
                }
              >
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="housekeeping">Housekeeping</MenuItem>
                <MenuItem value="frontDesk">Front Desk</MenuItem>
              </Select>
            </FormControl>
            <Box>
              {daysOfWeek.map((day) => (
                <Box key={day} display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!schedule[day]}
                        onChange={() => handleCheckboxChange(day)}
                      />
                    }
                    label={day}
                  />
                  {schedule[day] && (
                    <Box display="flex" ml={2}>
                      <TextField
                        label="Start Time"
                        type="time"
                        value={schedule[day].startTime}
                        onChange={(e) =>
                          handleTimeChange(day, "startTime", e.target.value)
                        }
                        margin="normal"
                      />
                      <TextField
                        label="End Time"
                        type="time"
                        value={schedule[day].endTime}
                        onChange={(e) =>
                          handleTimeChange(day, "endTime", e.target.value)
                        }
                        margin="normal"
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            {!selectedEmployee.userId && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PersonAdd />}
                onClick={() => {
                  handleEditClose();
                  handleOpenCreateUser(selectedEmployee);
                }}
                sx={{ mt: 2, mr: 1 }}
              >
                Create User Account
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleEditEmployee}
              sx={{ mt: 2 }}
            >
              Save Changes
            </Button>
            <Button
              variant="contained"
              onClick={() => handleRemoveEmployee(selectedEmployee.id)}
              sx={{ mt: 2, ml: 2 }}
            >
              Remove Employee
            </Button>
          </Paper>
        </Modal>
      )}

      <Modal open={createUserOpen} onClose={handleCloseCreateUser}>
        <Paper
          sx={{
            p: 4,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxHeight: "90vh",
            overflow: "auto"
          }}
        >
          <Typography variant="h5" gutterBottom>
            Create User Account
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            An invitation will be sent to {selectedEmployeeForUser?.name}. They will need to
            complete registration to activate their account.
          </Typography>

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={userCreationEmail}
            onChange={(e) => setUserCreationEmail(e.target.value)}
            margin="normal"
            required
            helperText="Enter the employee's email address"
          />

          <TextField
            fullWidth
            label="Employee Name"
            value={selectedEmployeeForUser?.name || ""}
            margin="normal"
            disabled
            helperText="This will be used as the user's full name"
          />

          <TextField
            fullWidth
            label="User Type"
            value={selectedEmployeeForUser?.type === "frontDesk" ? "Front Desk" :
                   selectedEmployeeForUser?.type.charAt(0).toUpperCase() +
                   selectedEmployeeForUser?.type.slice(1)}
            margin="normal"
            disabled
            helperText="User type will match employee type"
          />

          {message && (
            <Alert severity={message.includes("Error") ? "error" : "success"} sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleCreateUserFromEmployee}
              disabled={isCreatingUser || !userCreationEmail}
              fullWidth
            >
              {isCreatingUser ? "Sending Invitation..." : "Send Invitation"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseCreateUser}
              disabled={isCreatingUser}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default EmployeeSchedule;
